import React, { useState, useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { FriendsContext } from '../contexts/FriendsContext';
import Modal from './Modal';
import ScoreGraph from './ScoreGraph';
import './ConsolidatedPlayersList.css';
import {getTournamentTypeDisplay} from "../utils/constants"

/**
 * ConsolidatedPlayersList component displays a combined list of players from two different tournaments
 * 
 * @param {Object} props - Component props
 * @param {Object} props.currentGame - Current game data with leaderboard and type
 * @param {Object} props.matchingGame - Matching game data with leaderboard and type
 * @param {string} props.currentUsername - Username of the current user
 * @param {Function} props.onBackToOriginal - Callback to go back to original view
 */
function ConsolidatedPlayersList({ currentGame, matchingGame, currentUsername, onBackToOriginal, users }) {
  const { theme } = useContext(ThemeContext);
  const { isFriend, addFriend, removeFriend } = useContext(FriendsContext);
  const [sortBy, setSortBy] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);

  // Get user-friendly type names
  const getUserFriendlyType = (type) => {
    return getTournamentTypeDisplay(type)
  };

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to descending for score
      setSortBy(field);
      setSortDirection(field === 'score' ? 'desc' : 'asc');
    }
  };

  // Handle friend button click
  const handleFriendButtonClick = (username) => {
    if (isFriend(username)) {
      removeFriend(username);
    } else {
      addFriend(username);
    }
  };

  // Combine player lists from both games
  const allPlayers = [];

  // Add players from current game with type indicator
  currentGame.leaderboard.forEach(player => {
    allPlayers.push({
      ...player,
      type: currentGame.type
    });
  });

  // Add players from matching game with type indicator
  matchingGame.leaderboard.forEach(player => {
    allPlayers.push({
      ...player,
      type: matchingGame.type
    });
  });

  // Sort and filter players
  const sortedPlayers = [...allPlayers]
    .filter(player => 
      player.userName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'rank':
          comparison = a.rank - b.rank;
          break;
        case 'userName':
          comparison = a.userName.localeCompare(b.userName);
          break;
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'points':
          comparison = a.points - b.points;
          break;
        default:
          comparison = a.score - b.score;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

  return (
    <div className={`consolidated-players-list-container ${theme}`}>
      <div className="consolidated-header">
        <h3>Consolidated Players ({getUserFriendlyType(currentGame.type)} & {getUserFriendlyType(matchingGame.type)})</h3>
        <div className="consolidated-buttons">
          <button 
            className="back-to-original-button"
            onClick={onBackToOriginal}
          >
            Back to {getUserFriendlyType(currentGame.type)} Players Only
          </button>
          <button 
            className={`view-graph-button ${theme}`}
            onClick={() => setIsGraphModalOpen(true)}
          >
            View Score Graph
          </button>
        </div>
      </div>

      <div className="players-list-search">
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={theme}
        />
      </div>

      <div className="players-list-table-container">
        <table className={`players-list-table ${theme}`}>
          <thead>
            <tr>
              <th onClick={() => handleSort('rank')} className={sortBy === 'rank' ? 'active' : ''}>
                Rank {sortBy === 'rank' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('userName')} className={sortBy === 'userName' ? 'active' : ''}>
                Player {sortBy === 'userName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Type</th>
              <th onClick={() => handleSort('score')} className={sortBy === 'score' ? 'active' : ''}>
                Score {sortBy === 'score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              {sortedPlayers.some(player => player.initials) && (
                <th>Initials</th>
              )}
              <th onClick={() => handleSort('points')} className={sortBy === 'points' ? 'active' : ''}>
                Points {sortBy === 'points' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Friend</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => (
              <tr 
                key={`${player.userName}-${player.type}`} 
                className={`
                  ${player.userName.toLowerCase() === currentUsername?.toLowerCase() ? 'current-user' : ''}
                  type-${player.type}
                `}
              >
                <td>#{index + 1} (#{player.rank})</td>
                <td style={{ color: users?.[player.userName]?.background_color_hex }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {users?.[player.userName]?.avatar_path && (
                      <div 
                        className="player-avatar-circle" 
                        style={{ 
                          backgroundColor: users[player.userName].background_color_hex,
                          width: '24px',
                          height: '24px',
                          marginRight: '8px',
                          marginBottom: 0
                        }}
                      >
                        <img 
                          src={users[player.userName].avatar_path} 
                          alt={player.userName} 
                          style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    {player.userName}
                  </div>
                </td>
                <td>
                  <span className={`type-badge type-${player.type}`}>
                    {getUserFriendlyType(player.type)}
                  </span>
                </td>
                <td>{player.score?.toLocaleString()}</td>
                {sortedPlayers.some(player => player.initials) && (
                  <td>{player.initials || '-'}</td>
                )}
                <td>{player.points}</td>
                <td>
                  {player.userName.toLowerCase() !== currentUsername?.toLowerCase() && (
                    <button 
                      className={`friend-button ${isFriend(player.userName) ? 'remove-friend' : 'add-friend'}`}
                      onClick={() => handleFriendButtonClick(player.userName)}
                      title={isFriend(player.userName) ? 'Remove friend' : 'Add friend'}
                    >
                      {isFriend(player.userName) ? '✓' : '+'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sortedPlayers.length === 0 && (
              <tr>
                <td colSpan={sortedPlayers.some(player => player.initials) ? 7 : 6} className="no-results">
                  No players found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Score Graph Modal */}
      <Modal 
        isOpen={isGraphModalOpen} 
        onClose={() => setIsGraphModalOpen(false)} 
        title={`Score Distribution - ${currentGame.title || "Game"}`}
        size="large"
      >
        <ScoreGraph 
          players={currentGame.leaderboard} 
          currentUsername={currentUsername}
          matchingPlayers={matchingGame.leaderboard}
          gameTitle={currentGame.title || "Game"}
        />
      </Modal>
    </div>
  );
}

export default ConsolidatedPlayersList;
